import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Select,
  TextInput,
} from '../../ui';
import { useConfig } from '../../store/config';
import { SeriesProgress } from './SeriesProgress';
import {
  seriesAPI,
  seriesActionLabels,
  type SeriesAction,
  type SeriesStatus,
} from './seriesStatus';

export default function SeriesPanel({
  focusedSeries,
  onClearFocus,
}: {
  focusedSeries?: string | null;
  onClearFocus?: () => void;
}) {
  const { config } = useConfig();
  const [filter, setFilter] = useState<
    'active' | 'attention' | 'completed' | 'all'
  >('active');
  const [search, setSearch] = useState('');
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['series'],
    queryFn: seriesAPI.list,
    refetchInterval: 3000,
  });
  const needsAttention = (s: SeriesStatus) =>
    s.held ||
    ['failed', 'recovering', 'result_review'].includes(s.phase) ||
    s.blockers?.length > 0;
  const completed = (s: SeriesStatus) =>
    ['finished', 'cancelled'].includes(s.phase);
  const all = query.data?.series ?? [];
  const visible = all.filter((s) =>
    focusedSeries
      ? s.id === focusedSeries
      : (filter === 'all' ||
          (filter === 'attention'
            ? needsAttention(s)
            : filter === 'completed'
              ? completed(s)
              : !completed(s))) &&
        s.game_name.toLowerCase().includes(search.toLowerCase()),
  );
  const playerName = (id: string) =>
    config.players.find((p) => p.steamId === id)?.name || id;
  const [selection, setSelection] = useState<{
    series: SeriesStatus;
    action: string;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [team, setTeam] = useState('radiant');
  const [outgoing, setOutgoing] = useState('');
  const [incoming, setIncoming] = useState('');
  const [scope, setScope] = useState('current_game');
  const [minutes, setMinutes] = useState('5');
  const [pendingRequest, setPendingRequest] = useState<SeriesAction | null>(
    null,
  );
  const mutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: SeriesAction }) =>
      seriesAPI.action(id, action),
    onSuccess: () => {
      setSelection(null);
      setPendingRequest(null);
      void client.invalidateQueries({ queryKey: ['series'] });
      void client.invalidateQueries({ queryKey: ['lobbies'] });
    },
  });

  function open(series: SeriesStatus, action: string) {
    setSelection({ series, action });
    setReason('');
    setTeam('radiant');
    setOutgoing('');
    setIncoming('');
    setScope('current_game');
    setMinutes('5');
    setPendingRequest(null);
    mutation.reset();
  }

  function submit() {
    if (!selection || !reason.trim()) return;
    const action: SeriesAction = pendingRequest ?? {
      id: crypto.randomUUID(),
      expected_revision: selection.series.revision,
      type: selection.action,
      reason: reason.trim(),
      ...(selection.action === 'replace_player'
        ? { team, outgoing, incoming: incoming.trim(), scope }
        : {}),
      ...(['confirm_result', 'forfeit_game', 'forfeit_series'].includes(
        selection.action,
      )
        ? { winner: team }
        : {}),
      ...(selection.action === 'extend_deadline'
        ? { seconds: Number(minutes) * 60 }
        : {}),
    };
    setPendingRequest(action);
    mutation.mutate({ id: selection.series.id, action });
  }

  return (
    <Card
      title="Live series"
      description="Review exceptions first, then follow each match from check-in to result."
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" aria-label="Filter series">
          {(['active', 'attention', 'completed', 'all'] as const).map(
            (value) => (
              <Button
                key={value}
                size="sm"
                variant={
                  filter === value && !focusedSeries ? 'primary' : 'secondary'
                }
                onClick={() => {
                  setFilter(value);
                  onClearFocus?.();
                }}
              >
                {value === 'attention'
                  ? `Needs attention (${all.filter(needsAttention).length})`
                  : value === 'active'
                    ? `Active (${all.filter((s) => !completed(s)).length})`
                    : value === 'completed'
                      ? 'Completed'
                      : 'All series'}
              </Button>
            ),
          )}
        </div>
        <TextInput
          aria-label="Find series"
          placeholder="Find a match…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onClearFocus?.();
          }}
          className="sm:max-w-64"
        />
      </div>
      {focusedSeries && (
        <div className="mb-3 flex items-center gap-3 text-sm">
          <span>Showing selected fixture</span>
          <Button size="sm" onClick={onClearFocus}>
            Show all series
          </Button>
        </div>
      )}
      {query.isPending && (
        <p className="text-sm text-ink-dim">Loading series…</p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-warn">
          Series status unavailable: {query.error.message}
        </p>
      )}
      {query.data?.series.length === 0 && (
        <p className="text-sm text-ink-dim">No series created yet.</p>
      )}
      {all.length > 0 && visible.length === 0 && (
        <p className="py-8 text-center text-sm text-ink-dim">
          No series in this view. Choose another filter or create a series.
        </p>
      )}
      <ul className="divide-y divide-line">
        {visible.map((s) => (
          <li key={s.id} className="space-y-3 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <strong className="flex-1">{s.game_name}</strong>
              <Badge
                tone={
                  s.phase === 'finished'
                    ? 'ok'
                    : needsAttention(s)
                      ? 'warn'
                      : 'neutral'
                }
              >
                {s.held ? 'Held · ' : ''}
                {s.phase.replaceAll('_', ' ')}
              </Badge>
            </div>
            <SeriesProgress series={s} />
            {!s.durable && (
              <p className="text-xs text-warn">
                State is held in memory; PostgreSQL storage is unavailable.
              </p>
            )}
            {s.failure_reason && (
              <p className="text-sm text-warn">{s.failure_reason}</p>
            )}
            {s.blockers?.length > 0 && (
              <ul className="text-xs text-warn">
                {s.blockers.map((b, i) => (
                  <li key={i}>
                    {b.code.replaceAll('_', ' ')} {b.team} {b.steam_id}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {s.allowed_actions
                .filter(
                  (a) =>
                    !['cancel', 'forfeit_series', 'forfeit_game'].includes(a),
                )
                .map((a) => (
                  <Button
                    key={a}
                    size="sm"
                    variant={
                      [
                        'approve_ready',
                        'confirm_result',
                        'resume',
                        'resume_game',
                      ].includes(a)
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={() => open(s, a)}
                  >
                    {seriesActionLabels[a] ?? a}
                  </Button>
                ))}
              {s.allowed_actions.some((a) =>
                ['cancel', 'forfeit_series', 'forfeit_game'].includes(a),
              ) && (
                <details className="relative text-sm">
                  <summary className="cursor-pointer px-3 py-2 text-ink-dim">
                    More actions
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {s.allowed_actions
                      .filter((a) =>
                        ['cancel', 'forfeit_series', 'forfeit_game'].includes(
                          a,
                        ),
                      )
                      .map((a) => (
                        <Button
                          key={a}
                          size="sm"
                          variant="danger"
                          onClick={() => open(s, a)}
                        >
                          {seriesActionLabels[a] ?? a}
                        </Button>
                      ))}
                  </div>
                </details>
              )}
            </div>
            <details className="text-xs text-ink-dim">
              <summary className="cursor-pointer">Lineups and history</summary>
              <p className="mt-2">
                Team A:{' '}
                {s.teams.radiant?.map(playerName).join(', ') || 'Open lineup'}
              </p>
              <p>
                Team B:{' '}
                {s.teams.dire?.map(playerName).join(', ') || 'Open lineup'}
              </p>
              <p>
                Series {s.id} · Bot {s.bot_id || 'Unassigned'}
                {s.lobby_id ? ` · Lobby ${s.lobby_id}` : ''}
              </p>
              {s.games?.map((g) => (
                <p key={`${g.number}:${g.attempt}`}>
                  Game {g.number}/{g.attempt}: {g.resolution}{' '}
                  {g.winner
                    ? `· ${g.winner === 'radiant' ? 'Team A' : 'Team B'} wins`
                    : ''}
                  {g.match_id ? ` · Match ${g.match_id}` : ''}
                </p>
              ))}
              {s.actions?.map((a) => (
                <p key={a.id}>
                  {new Date(a.at).toLocaleString()} ·{' '}
                  {seriesActionLabels[a.type] ?? a.type} · {a.reason} ·{' '}
                  {a.actor}
                </p>
              ))}
            </details>
          </li>
        ))}
      </ul>
      <Dialog
        open={selection !== null}
        onClose={() => !mutation.isPending && setSelection(null)}
        title={seriesActionLabels[selection?.action ?? ''] ?? 'Series action'}
        footer={
          <Button
            variant="primary"
            disabled={!reason.trim()}
            loading={mutation.isPending}
            onClick={submit}
          >
            {pendingRequest ? 'Retry same action' : 'Apply action'}
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-dim">
            {selection?.series.game_name}. Holding automation does not pause a
            running Dota game.
          </p>
          {[
            'replace_player',
            'confirm_result',
            'forfeit_game',
            'forfeit_series',
          ].includes(selection?.action ?? '') && (
            <Field
              label={
                selection?.action === 'replace_player' ? 'Team' : 'Winning team'
              }
            >
              {({ id }) => (
                <Select
                  id={id}
                  value={team}
                  disabled={!!pendingRequest}
                  onChange={(e) => {
                    setTeam(e.target.value);
                    setOutgoing('');
                  }}
                >
                  <option value="radiant">
                    {selection?.series.team_a_name || 'Team A'}
                  </option>
                  <option value="dire">
                    {selection?.series.team_b_name || 'Team B'}
                  </option>
                </Select>
              )}
            </Field>
          )}
          {selection?.action === 'replace_player' && (
            <>
              <Field label="Outgoing starter">
                {({ id }) => (
                  <Select
                    id={id}
                    value={outgoing}
                    disabled={!!pendingRequest}
                    onChange={(e) => setOutgoing(e.target.value)}
                  >
                    <option value="">Select starter</option>
                    {selection.series.teams[team as 'radiant' | 'dire']?.map(
                      (id) => (
                        <option key={id}>{id}</option>
                      ),
                    )}
                  </Select>
                )}
              </Field>
              <Field label="Incoming Steam ID">
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={incoming}
                    disabled={!!pendingRequest}
                    onChange={(e) => setIncoming(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Scope">
                {({ id }) => (
                  <Select
                    id={id}
                    value={scope}
                    disabled={!!pendingRequest}
                    onChange={(e) => setScope(e.target.value)}
                  >
                    <option value="current_game">This game</option>
                    <option value="remaining_series">Remaining series</option>
                  </Select>
                )}
              </Field>
            </>
          )}
          {selection?.action === 'extend_deadline' && (
            <Field label="Additional minutes">
              {({ id }) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  max={1440}
                  value={minutes}
                  disabled={!!pendingRequest}
                  onChange={(e) => setMinutes(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field label="Reason">
            {({ id }) => (
              <TextInput
                id={id}
                value={reason}
                disabled={!!pendingRequest}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {mutation.isError && (
            <div role="alert" className="text-sm text-warn">
              <p>{mutation.error.message}</p>
              <Button
                onClick={() => {
                  setSelection(null);
                  void query.refetch();
                }}
              >
                Close and refresh status
              </Button>
            </div>
          )}
        </div>
      </Dialog>
    </Card>
  );
}
